import React, { useState } from 'react';
import './ReviewCard.css';
import reviewService from '../services/reviewService';
import { useAuth } from '../context/AuthContext';

const ReviewCard = ({ review, onUpdate, onDelete }) => {
  const { user, token } = useAuth();
  const [isHelpful, setIsHelpful] = useState(false);
  const [helpfulCount, setHelpfulCount] = useState(review.helpfulCount || 0);
  const [isSubmittingHelpful, setIsSubmittingHelpful] = useState(false);

  const handleMarkHelpful = async () => {
    if (!user || !token) {
      alert('Please login to mark reviews as helpful');
      return;
    }

    setIsSubmittingHelpful(true);
    try {
      const newHelpfulState = !isHelpful;
      const response = await reviewService.markHelpful(review._id, newHelpfulState, token);
      
      if (response.success) {
        setIsHelpful(newHelpfulState);
        setHelpfulCount(response.data.helpfulCount);
      }
    } catch (error) {
      console.error('Error marking review helpful:', error);
      alert('Failed to update helpful status');
    } finally {
      setIsSubmittingHelpful(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <i
        key={index}
        className={`fas fa-star ${index < rating ? 'filled' : 'empty'}`}
      />
    ));
  };

  return (
    <div className="review-card">
      <div className="review-header">
        <div className="reviewer-info">
          <div className="reviewer-avatar">
            {review.user?.avatar ? (
              <img src={review.user.avatar} alt={review.user.name || review.customerName} />
            ) : (
              <div className="avatar-placeholder">
                <i className="fas fa-user"></i>
              </div>
            )}
          </div>
          <div className="reviewer-details">
            <h4 className="reviewer-name">{review.user?.name || review.customerName || 'Anonymous'}</h4>
            <div className="review-meta">
              <div className="review-rating">
                {renderStars(review.rating)}
              </div>
              <span className="review-date">{formatDate(review.createdAt)}</span>
              {review.verified && (
                <span className="verified-badge">
                  <i className="fas fa-check-circle"></i>
                  Verified Purchase
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="review-content">
        <h3 className="review-title">{review.title}</h3>
        <p className="review-comment">{review.comment}</p>
        
        {review.images && review.images.length > 0 && (
          <div className="review-images">
            {review.images.map((image, index) => (
              <img
                key={index}
                src={image.url}
                alt={image.alt || `Review image ${index + 1}`}
                className="review-image"
              />
            ))}
          </div>
        )}
      </div>

      {review.adminResponse && (
        <div className="admin-response">
          <div className="admin-response-header">
            <i className="fas fa-shield-alt"></i>
            <span>Admin Response</span>
          </div>
          <p className="admin-response-text">{review.adminResponse.comment}</p>
          <div className="admin-response-meta">
            <span>By {review.adminResponse.respondedBy?.name}</span>
            <span>{formatDate(review.adminResponse.respondedAt)}</span>
          </div>
        </div>
      )}

      <div className="review-actions">
        <button
          className={`helpful-btn ${isHelpful ? 'active' : ''}`}
          onClick={handleMarkHelpful}
          disabled={isSubmittingHelpful}
        >
          <i className="fas fa-thumbs-up"></i>
          <span>Helpful ({helpfulCount})</span>
        </button>

        {user && user._id === review.user?._id && (
          <div className="review-owner-actions">
            <button
              className="edit-review-btn"
              onClick={() => onUpdate && onUpdate(review)}
            >
              <i className="fas fa-edit"></i>
              Edit
            </button>
            <button
              className="delete-review-btn"
              onClick={() => onDelete && onDelete(review)}
            >
              <i className="fas fa-trash"></i>
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewCard;
